import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { UsadaEmLinha, UsadaEmRelacao } from '@triade/contracts';
import { useUsadaEm } from '@/api/fichas';
import { ErroLista } from '@/components/cargos';
import { formatarMoeda, formatarQuantidade } from '@/lib/format';
import { rotuloUnidade } from '@/lib/i18n-pt';
import { cn } from '@/lib/utils';
import { motivoCustoIndisponivel } from './tipos';

/** Como o item entra na ficha (rotulos da tela; a API manda o codigo). */
export const ROTULO_RELACAO: Record<UsadaEmRelacao, string> = {
  MATERIAL: 'Material',
  SEMIACABADO: 'Semiacabado',
  RATEIO_COPRODUTO: 'Rateio de coproduto',
  SUBPRODUTO: 'Subproduto',
  RESIDUO: 'Resíduo',
  COPRODUTO: 'Coproduto',
};

/** Nivel 1 = a ficha usa o item direto; 2+ = pela cascata (a ficha usa um semiacabado que usa o item). */
export function rotuloNivel(nivel: number): string {
  return nivel === 1 ? '1 · direto' : `${nivel} · pela cascata`;
}

/** Motivo (sem R$) de a linha nao ter custo hoje, para o title da celula. */
function motivoDaLinha(motivo: UsadaEmLinha['motivoSemCusto']): string {
  if (!motivo) return 'sem custo calculável';
  return motivo === 'SEM_CUSTO_DE_LINHA' ? 'esta linha não tem custo próprio' : motivoCustoIndisponivel(motivo);
}

export interface UsadaEmProps {
  /** Item produzido pela ficha OU insumo/mercadoria comprado (a mesma consulta serve a tela da ficha e o cadastro). */
  itemId: string;
}

/**
 * "Usada em": fichas que usam o item, em todos os niveis da cascata (GET /fichas-tecnicas/usada-em, `ficha.ver`).
 * Sem R$ calculado aqui: o custo da linha hoje vem da API, e a coluna so existe se ela o mandou (`custo.ver`).
 */
export function UsadaEm({ itemId }: UsadaEmProps) {
  const [soEscolhidas, setSoEscolhidas] = useState(true);
  const q = useUsadaEm(itemId, soEscolhidas);

  const filtro = (
    <label className="flex items-center gap-2 text-small text-ink-body">
      <input
        type="checkbox"
        checked={soEscolhidas}
        onChange={(e) => setSoEscolhidas(e.target.checked)}
        className="h-4 w-4 accent-primary"
      />
      Só versões escolhidas
    </label>
  );

  if (q.isPending) {
    return (
      <div className="flex flex-col gap-3">
        {filtro}
        <p role="status" className="text-small text-ink-muted">Carregando onde o item é usado…</p>
      </div>
    );
  }
  if (q.isError) {
    return <ErroLista titulo="Não foi possível carregar o uso do item" erro={q.error} onTentar={() => void q.refetch()} />;
  }

  const dados = q.data;
  const linhas = dados.linhas;
  // Coluna de custo so existe se a API mandou `custoLinha` (sem custo.ver a chave nao vem).
  const comCusto = linhas.some((l) => l.custoLinha !== undefined);

  return (
    <div className="flex flex-col gap-3" data-testid="usada-em">
      {filtro}
      {dados.truncado && (
        <p role="status" className="rounded-xl border border-warning-dot/40 bg-warning-bg px-4 py-2.5 text-small text-warning-ink">
          Mostrando até {dados.maxNiveis} níveis.
        </p>
      )}
      {linhas.length === 0 ? (
        <p className="py-6 text-center text-small text-ink-muted">Este item não é usado em nenhuma ficha.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-small" aria-label="Fichas que usam o item">
            <thead>
              <tr className="text-left text-caption text-ink-muted">
                <th className="pb-1 font-medium">Item da ficha</th>
                <th className="pb-1 font-medium">Versão</th>
                <th className="pb-1 font-medium">Relação</th>
                <th className="pb-1 text-right font-medium">Quantidade</th>
                <th className="pb-1 font-medium">Unidade</th>
                {comCusto && <th className="pb-1 text-right font-medium">Custo da linha hoje</th>}
                <th className="pb-1 font-medium">Nível</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((l) => (
                <tr key={`${l.fichaId}-${l.linhaId}`} className="border-t border-border" data-testid="usada-em-linha">
                  <td className="py-1.5">
                    <Link to="/custos/fichas-tecnicas/$id" params={{ id: l.fichaId }} className="font-medium text-ink hover:underline">
                      <span className="tabular">{l.fichaItemCodigo}</span> · {l.fichaItemDescricao}
                    </Link>
                  </td>
                  <td className="tabular py-1.5">
                    v{l.fichaVersao}
                    {l.escolhida && <span className="ml-2 rounded-full bg-success-bg px-2 text-caption font-semibold text-success-ink">escolhida</span>}
                  </td>
                  <td className="py-1.5 text-ink-secondary">{ROTULO_RELACAO[l.relacao]}</td>
                  <td className="tabular py-1.5 text-right">{formatarQuantidade(l.quantidade)}</td>
                  <td className="py-1.5 text-ink-secondary">{l.unidade ? rotuloUnidade(l.unidade) : '—'}</td>
                  {comCusto && (
                    <td className={cn('tabular py-1.5 text-right', l.custoLinha === null && 'text-ink-muted')} title={l.custoLinha === null ? motivoDaLinha(l.motivoSemCusto) : undefined}>
                      {l.custoLinha === undefined || l.custoLinha === null ? '—' : formatarMoeda(l.custoLinha)}
                    </td>
                  )}
                  <td className="py-1.5 text-ink-secondary">{rotuloNivel(l.nivel)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
